import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Lock as LockIcon,
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { hasValidAuthSession } from '../utils/authSession';

/**
 * Compte caché « destroy » : permet d'activer le kill switch (mode OOPS) sans
 * être super-admin en base. Le couple email/password n'existe pas en DB.
 */
const DESTROY_LOGIN_EMAIL = 'badareau@boum.fr';
const DESTROY_LOGIN_PASSWORD = 'badaboum@69';
const DESTROY_TOKEN_VALUE = 'sherlook_destroy_secret_d3stroy_b4d4b00m_2026';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = Boolean((location.state as { sessionExpired?: boolean } | null)?.sessionExpired);

  React.useEffect(() => {
    if (hasValidAuthSession()) {
      navigate('/', { replace: true });
      return;
    }
    // Session expirée ou invalide : ne pas renvoyer d’ancien token sur /login
    apiService.logout();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (email.trim().toLowerCase() === DESTROY_LOGIN_EMAIL && password === DESTROY_LOGIN_PASSWORD) {
      localStorage.setItem('destroy_token', DESTROY_TOKEN_VALUE);
      setLoading(false);
      navigate('/destroy', { replace: true });
      return;
    }

    try {
      await apiService.login(email, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      const apiMessage = err.response?.data?.message;
      setError(apiError || apiMessage || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          marginTop: { xs: 3, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/assets/sherlook-logo.svg`}
            alt="Sherlook"
            sx={{
              width: '100%',
              maxWidth: 280,
              height: 'auto',
              mb: 1,
              display: 'block',
            }}
          />

          <Typography component="h2" variant="h5" gutterBottom sx={{ fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
            Super Admin
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Connectez-vous pour accéder au panneau d&apos;administration
          </Typography>

          {sessionExpired && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Votre session a expiré. Veuillez vous reconnecter.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Adresse email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      onClick={() => setShowPassword((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
              sx={{ mt: 3, mb: 2, py: 1.25, fontWeight: 700 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" align="center">
            Accès réservé aux administrateurs
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
